import * as Sentry from '@sentry/react';

/**
 * Configuration Sentry pour monitoring erreurs production
 * 
 * Initialise Sentry avec:
 * - Capture automatique erreurs React
 * - Replay sessions (pour debug visuel)
 * - Performance monitoring
 * - Breadcrumbs (historique actions utilisateur)
 * 
 * Variables environnement requises:
 * - VITE_SENTRY_DSN: URL projet Sentry
 * - VITE_APP_ENV: Environment (production, staging, dev)
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const APP_ENV = import.meta.env.VITE_APP_ENV || 'production';
const IS_PRODUCTION = APP_ENV === 'production';

// N'initialiser Sentry qu'en production et si DSN fourni
if (SENTRY_DSN && IS_PRODUCTION) {
  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Environment (production, staging, etc.)
    environment: APP_ENV,
    
    // Taux échantillonnage erreurs (100% = toutes les erreurs)
    sampleRate: 1.0,
    
    // Performance Monitoring
    integrations: [
      // React component tracing
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation: () => window.location,
        useNavigationType: () => 'POP',
        createRoutesFromChildren: () => [],
        matchRoutes: () => [],
      }),
      
      // Session Replay - enregistre sessions avec erreurs
      Sentry.replayIntegration({
        maskAllText: true, // Masquer textes sensibles
        blockAllMedia: true, // Bloquer images/vidéos
      }),
      
      // Breadcrumbs enrichis
      Sentry.breadcrumbsIntegration({
        console: true, // Logs console
        dom: true, // Clics DOM
        fetch: true, // Requêtes fetch
        history: true, // Navigation
        sentry: true, // Events Sentry
        xhr: true, // Requêtes XMLHttpRequest
      }),
    ],
    
    // Performance Monitoring - Taux échantillonnage transactions
    tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0, // 10% en prod, 100% en dev
    
    // Session Replay - Taux échantillonnage
    replaysSessionSampleRate: 0.1, // 10% sessions normales
    replaysOnErrorSampleRate: 1.0, // 100% sessions avec erreurs
    
    // Filtrer certaines erreurs (bruits)
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Ignorer erreurs réseau temporaires
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message).toLowerCase();
        
        // Erreurs réseau à ignorer
        if (
          message.includes('network error') ||
          message.includes('failed to fetch') ||
          message.includes('networkerror') ||
          message.includes('load failed')
        ) {
          return null; // Ne pas envoyer à Sentry
        }
        
        // Erreurs extension navigateur à ignorer
        if (
          message.includes('extension') ||
          message.includes('chrome-extension') ||
          message.includes('moz-extension')
        ) {
          return null;
        }
      }
      
      return event;
    },
    
    // Enrichir contexte erreur
    beforeBreadcrumb(breadcrumb, hint) {
      // Masquer données sensibles dans breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.message) {
        // Masquer tokens, mots de passe dans logs
        breadcrumb.message = breadcrumb.message.replace(
          /\b([a-zA-Z0-9_-]{20,})\b/g,
          '[REDACTED_TOKEN]'
        );
      }
      
      return breadcrumb;
    },
    
    // Tags globaux (filtres Sentry Dashboard)
    initialScope: {
      tags: {
        app: 'nazandoo-frontend',
      },
    },
  });
  
  console.log('✅ Sentry monitoring activé -', APP_ENV);
} else if (!SENTRY_DSN) {
  console.warn('⚠️ VITE_SENTRY_DSN non défini - Monitoring Sentry désactivé');
} else {
  console.log('ℹ️ Sentry désactivé en', APP_ENV);
}

/**
 * Helper: Capturer erreur manuellement
 * 
 * Usage:
 * ```typescript
 * import { captureError } from '@/lib/sentry';
 * 
 * try {
 *   // code risqué
 * } catch (error) {
 *   captureError(error, { context: 'payment', userId: user.id });
 * }
 * ```
 */
export const captureError = (
  error: unknown,
  context?: Record<string, any>
) => {
  if (SENTRY_DSN && IS_PRODUCTION) {
    Sentry.captureException(error, {
      contexts: context ? { custom: context } : undefined,
    });
  } else {
    console.error('Error:', error, context);
  }
};

/**
 * Helper: Capturer message custom
 * 
 * Usage:
 * ```typescript
 * import { captureMessage } from '@/lib/sentry';
 * 
 * captureMessage('Paiement échoué 3 fois', 'warning', { 
 *   userId: user.id,
 *   amount: 50000 
 * });
 * ```
 */
export const captureMessage = (
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  context?: Record<string, any>
) => {
  if (SENTRY_DSN && IS_PRODUCTION) {
    Sentry.captureMessage(message, {
      level,
      contexts: context ? { custom: context } : undefined,
    });
  } else {
    console.log(`[${level.toUpperCase()}]`, message, context);
  }
};

/**
 * Helper: Définir contexte utilisateur
 * 
 * Usage:
 * ```typescript
 * import { setUserContext } from '@/lib/sentry';
 * 
 * // Après login
 * setUserContext({
 *   id: user.id,
 *   email: user.email,
 *   role: user.system_role,
 * });
 * 
 * // Après logout
 * setUserContext(null);
 * ```
 */
export const setUserContext = (user: {
  id: string;
  email?: string;
  role?: string;
} | null) => {
  if (SENTRY_DSN && IS_PRODUCTION) {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.role,
      });
    } else {
      Sentry.setUser(null);
    }
  }
};

/**
 * Helper: Ajouter tag custom
 * 
 * Usage:
 * ```typescript
 * import { addTag } from '@/lib/sentry';
 * 
 * addTag('payment_method', 'stripe');
 * addTag('currency', 'CDF');
 * ```
 */
export const addTag = (key: string, value: string) => {
  if (SENTRY_DSN && IS_PRODUCTION) {
    Sentry.setTag(key, value);
  }
};

/**
 * HOC: Wrapper component avec error boundary Sentry
 * 
 * Usage:
 * ```typescript
 * import { withSentryErrorBoundary } from '@/lib/sentry';
 * 
 * const MyComponent = () => { ... };
 * 
 * export default withSentryErrorBoundary(MyComponent, {
 *   fallback: <ErrorFallback />,
 *   showDialog: true,
 * });
 * ```
 */
export const withSentryErrorBoundary = Sentry.withErrorBoundary;

/**
 * Component: Error Boundary Sentry
 * 
 * Usage:
 * ```tsx
 * import { SentryErrorBoundary } from '@/lib/sentry';
 * 
 * <SentryErrorBoundary fallback={<ErrorPage />}>
 *   <App />
 * </SentryErrorBoundary>
 * ```
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Export Sentry instance pour usage avancé
export default Sentry;
